import { countMaskCells, type Mask } from './mask';
import type { StencilManifestEntry } from './manifest';
import { processStencil } from './process';

/**
 * Stencil solidity: the share of a silhouette's cells that are fully surrounded.
 *
 * This is the property that decides whether a stencil produces a *puzzle*. Thin silhouettes
 * — letters, spirals, anything drawn with strokes — put few arrows in any one row or column,
 * so almost nothing blocks anything and the board becomes a list of independent taps. Solid
 * silhouettes pack arrows into shared rows and columns, which is where entanglement, and the
 * game, comes from.
 *
 * Measured on the same 141-level grid at matched size and recipe:
 *
 *   silhouette   solidity   open ratio   depth   blockers per arrow
 *   letters          0.30         0.63     4.1                 0.63
 *   icons            0.62         0.62     4.1                 0.60
 *   shapes           0.53         0.59     4.3                 0.72
 *   rectangle        0.80         0.47     6.1                 1.19
 */

/** Fixed reference grid, so a stencil's rating never depends on the level measuring it. */
const REFERENCE_WIDTH = 20;
const REFERENCE_HEIGHT = 24;

export function maskSolidity(mask: Mask): number {
  const active = countMaskCells(mask);
  if (active === 0) return 0;

  const { width, height, cells } = mask;
  const isActive = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < width && y < height && cells[y * width + x] === 1;

  let interior = 0;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (cells[y * width + x] !== 1) continue;
      if (isActive(x - 1, y) && isActive(x + 1, y) && isActive(x, y - 1) && isActive(x, y + 1)) {
        interior += 1;
      }
    }
  }
  return interior / active;
}

/**
 * Share of the reference grid the silhouette covers.
 *
 * Separate from solidity: a diamond is perfectly solid yet fills only half its grid, and a
 * square silhouette in a tall grid loses the rest to aspect-preserving fitting. Coverage is
 * what decides how much board a level actually gets to use.
 */
export function stencilCoverage(entry: StencilManifestEntry): number {
  const cached = coverageCache.get(entry.id);
  if (cached !== undefined) return cached;

  const width = Math.max(REFERENCE_WIDTH, entry.minimumGridWidth);
  const height = Math.max(REFERENCE_HEIGHT, entry.minimumGridHeight);
  const result = processStencil(entry, {
    gridWidth: width,
    gridHeight: height,
    rotation: 0,
    mirrorHorizontal: false,
    mirrorVertical: false,
  });
  const coverage = result.ok ? result.activeCells / (width * height) : 0;
  coverageCache.set(entry.id, coverage);
  return coverage;
}

const coverageCache = new Map<string, number>();
const cache = new Map<string, number>();

/**
 * Solidity of a stencil at the reference grid, memoised.
 *
 * A stencil that cannot be processed at the reference size rates 0, which keeps it out of
 * selection rather than letting it through untested.
 */
export function stencilSolidity(entry: StencilManifestEntry): number {
  const cached = cache.get(entry.id);
  if (cached !== undefined) return cached;

  const result = processStencil(entry, {
    gridWidth: Math.max(REFERENCE_WIDTH, entry.minimumGridWidth),
    gridHeight: Math.max(REFERENCE_HEIGHT, entry.minimumGridHeight),
    rotation: 0,
    mirrorHorizontal: false,
    mirrorVertical: false,
  });
  const solidity = result.ok ? maskSolidity(result.mask) : 0;
  cache.set(entry.id, solidity);
  return solidity;
}
